'use client';

import { Children, isValidElement, type ReactNode } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { slugify } from '../helpers/slugify';
import { splitIntoSections } from '../helpers/split-into-sections';
import { ArticleCallout, type ArticleCalloutType } from './article-callout';
import { ArticleCodeBlock } from './article-code-block';
import { ArticleImage } from './article-image';
import { ArticleVideo } from './article-video';
import { ArticleYoutube, youtubeIdFromUrl } from './article-youtube';
import { ArticleImagesProvider } from './article-images-context';
import { ArticleSectionShare } from './article-section-share';

/** Flatten a React node tree to its plain-text content (for heading ids / prefix sniffing). */
const flattenToText = (node: ReactNode): string => {
	if (node == null || typeof node === 'boolean') return '';
	if (typeof node === 'string' || typeof node === 'number') return String(node);
	if (Array.isArray(node)) return node.map(flattenToText).join('');
	if (isValidElement(node)) {
		return flattenToText((node.props as { children?: ReactNode }).children);
	}
	return '';
};

/**
 * GitHub-style alerts at the start of a blockquote:
 *   - `[!NOTE]` / `[!TIP]` / `[!IMPORTANT]` / `[!WARNING]` — body follows inline.
 *   - `[!TLDR]` — the article's Summary (TL;DR) card; body follows on the next
 *     lines (normally a short bullet list). Docs require one at the top of
 *     every article (enforced by packages/docs/scripts/build-docs.mjs).
 *   - `[!DEFINITION] <term>` — the rest of the marker line becomes the callout
 *     title; the body follows on the next line. Renders as a filled accent card.
 */
const BASIC_PREFIX = /^\s*\[!(NOTE|TIP|IMPORTANT|WARNING|TLDR)\]\s*/i;
const DEFN_DETECT = /^\s*\[!DEFINITION\]([^\n]*)/i;
const DEFN_STRIP = /^\s*\[!DEFINITION\][^\n]*\r?\n?/i;

const VIDEO_EXT_RE = /\.(mp4|webm|mov|ogv|ogg)(?:[?#]|$)/i;

const detectCallout = (
	children: ReactNode
): { type: ArticleCalloutType; title?: string; stripped: ReactNode } | null => {
	const text = flattenToText(children);

	const defn = DEFN_DETECT.exec(text);
	const basic = defn ? null : BASIC_PREFIX.exec(text);
	if (!defn && !basic) return null;

	const type: ArticleCalloutType = defn
		? 'definition'
		: (basic![1].toLowerCase() as ArticleCalloutType);
	const title = defn ? defn[1].trim() || undefined : undefined;
	const stripRe = defn ? DEFN_STRIP : BASIC_PREFIX;

	// Strip the marker (and, for definitions, the title) from the first text node.
	let stripped = false;
	const strip = (node: ReactNode): ReactNode => {
		if (stripped) return node;
		if (typeof node === 'string') {
			const m = stripRe.exec(node);
			if (m) {
				stripped = true;
				return node.slice(m[0].length);
			}
			return node;
		}
		if (Array.isArray(node)) return node.map(strip);
		if (isValidElement(node)) {
			const el = node as React.ReactElement<{ children?: ReactNode }>;
			return { ...el, props: { ...el.props, children: strip(el.props.children) } };
		}
		return node;
	};

	return { type, title, stripped: strip(children) };
};

const components: Components = {
	h1: ({ children }) => (
		<h1 className="mb-[14px] mt-0 text-[34px] font-bold leading-[1.1] tracking-[-0.025em] text-neutral">
			{children}
		</h1>
	),
	h2: ({ children }) => {
		const id = slugify(flattenToText(children));
		return (
			<h2
				id={id}
				className="mb-[16px] mt-0 scroll-mt-24 text-[22px] font-semibold tracking-[-0.018em] text-neutral"
			>
				{children}
			</h2>
		);
	},
	h3: ({ children }) => {
		const id = slugify(flattenToText(children));
		return (
			<h3
				id={id}
				className="mb-[12px] mt-[40px] scroll-mt-24 text-[17px] font-semibold tracking-[-0.012em] text-neutral"
			>
				{children}
			</h3>
		);
	},
	h4: ({ children }) => (
		<h4 className="mb-2 mt-6 text-[15px] font-semibold text-neutral">{children}</h4>
	),
	p: ({ node, children }) => {
		// An image-only paragraph renders ArticleImage <figure>s, which are
		// invalid inside <p> — unwrap to avoid hydration errors. Consecutive
		// image lines (2+ images in one paragraph) render as a gallery grid;
		// a lone image keeps the plain unwrapped figure.
		const imageCount =
			node?.children.filter((child) => child.type === 'element' && child.tagName === 'img')
				.length ?? 0;
		const isImageOnly =
			imageCount > 0 &&
			!!node?.children.every(
				(child) =>
					(child.type === 'element' && child.tagName === 'img') ||
					(child.type === 'text' && child.value.trim() === '')
			);
		if (isImageOnly && imageCount >= 2) {
			// Drop the whitespace text nodes between the rendered figures.
			const images = Children.toArray(children).filter((child) => isValidElement(child));
			return (
				<div className="my-6 grid grid-cols-1 gap-x-5 gap-y-4 sm:grid-cols-2 [&>figure]:my-0">
					{images}
				</div>
			);
		}
		if (isImageOnly) return <>{children}</>;
		return <p className="mb-4 text-[15.5px] leading-[1.65] text-neutral/60">{children}</p>;
	},
	a: ({ href, children }) => {
		const isExternal = !!href && /^https?:\/\//.test(href);
		return (
			<a
				href={href}
				className="text-neutral underline underline-offset-2 hover:opacity-80"
				{...(isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
			>
				{children}
			</a>
		);
	},
	ul: ({ children }) => (
		<ul className="mb-4 ml-5 list-disc space-y-1.5 text-[15.5px] leading-[1.65] text-neutral/60 marker:text-neutral/40">
			{children}
		</ul>
	),
	ol: ({ children }) => (
		<ol className="mb-4 ml-5 list-decimal space-y-1.5 text-[15.5px] leading-[1.65] text-neutral/60 marker:text-neutral/40">
			{children}
		</ol>
	),
	li: ({ children }) => <li className="pl-1 [&>p]:mb-0">{children}</li>,
	strong: ({ children }) => <strong className="font-semibold text-neutral">{children}</strong>,
	em: ({ children }) => <em className="italic">{children}</em>,
	code: ({ className, children, ...props }) => {
		// `inline` is not reliably set by react-markdown v9; fenced code arrives with a
		// `language-*` className. Treat anything without that as inline.
		const match = /language-(\w+)/.exec(className ?? '');
		const isInline = !className?.includes('language-') && !String(children).includes('\n');
		if (isInline) {
			return (
				<code
					className="rounded bg-neutral/10 px-1 py-0.5 font-mono text-[0.9em] text-neutral"
					{...props}
				>
					{children}
				</code>
			);
		}
		const code = String(children).replace(/\n$/, '');
		return <ArticleCodeBlock code={code} language={match?.[1]} />;
	},
	// `pre` wraps fenced code — the `code` renderer already emits a full ArticleCodeBlock,
	// so pass children straight through to avoid a nested <pre>.
	pre: ({ children }) => <>{children}</>,
	blockquote: ({ children }) => {
		const callout = detectCallout(children);
		if (callout) {
			return (
				<ArticleCallout type={callout.type} title={callout.title}>
					{callout.stripped}
				</ArticleCallout>
			);
		}
		return (
			<blockquote className="my-[26px] border-l-2 border-neutral/10 pl-4 text-[15.5px] italic leading-[1.65] text-neutral/60">
				{children}
			</blockquote>
		);
	},
	hr: () => <hr className="my-8 border-0 border-t border-neutral/10" />,
	// Caption comes from the markdown title: ![alt](url "Caption") — mandatory
	// for docs images (enforced by build-docs.mjs).
	// When the URL points at a video file, render an inline player instead of an
	// image figure.
	img: ({ src, alt, title }) => {
		if (typeof src !== 'string') return null;
		if (youtubeIdFromUrl(src)) {
			return <ArticleYoutube src={src} caption={title || undefined} alt={alt ?? ''} />;
		}
		if (VIDEO_EXT_RE.test(src)) {
			return <ArticleVideo src={src} caption={title || undefined} alt={alt ?? ''} />;
		}
		return <ArticleImage src={src} alt={alt ?? ''} caption={title || undefined} />;
	},
	table: ({ children }) => (
		<div className="my-6 overflow-x-auto">
			<table className="w-full border-collapse overflow-hidden rounded-lg border border-neutral/10 text-[13.5px]">
				{children}
			</table>
		</div>
	),
	thead: ({ children }) => <thead className="bg-surface">{children}</thead>,
	tbody: ({ children }) => <tbody>{children}</tbody>,
	tr: ({ children }) => <tr className="border-b border-neutral/10 last:border-0">{children}</tr>,
	th: ({ children }) => (
		<th className="px-3 py-2 text-left font-semibold text-neutral">{children}</th>
	),
	td: ({ children }) => <td className="px-3 py-2 align-top text-neutral/60">{children}</td>,
};

/**
 * ArticleContent — renders a markdown article body with the shared block styling
 * used by both the docs site and the blog.
 *
 * Heading ids use the shared slugify() so the article TOC anchors line up with
 * the rendered `## ` headings. GitHub-style `> [!NOTE]` blockquotes become an
 * ArticleCallout, `> [!DEFINITION] term` becomes a filled definition card, and
 * fenced code becomes the dark ArticleCodeBlock. Each `## ` section gets a hover
 * deep-link affordance.
 */
export const ArticleContent = ({ body }: { body: string }): React.JSX.Element => {
	const sections = splitIntoSections(body);
	return (
		<ArticleImagesProvider body={body}>
			<div className="[&>*:first-child]:mt-0">
			{sections.map((section, index) => {
				if (section.id === null) {
					return (
						<ReactMarkdown key={index} remarkPlugins={[remarkGfm]} components={components}>
							{section.content}
						</ReactMarkdown>
					);
				}
				return (
					<section key={index} className="group/section relative mt-14 first:mt-0">
						<div className="absolute right-full top-0 mr-2 hidden opacity-0 transition-opacity group-hover/section:opacity-100 focus-within:opacity-100 sm:block">
							<ArticleSectionShare sectionId={section.id} />
						</div>
						<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
							{section.content}
						</ReactMarkdown>
					</section>
				);
			})}
			</div>
		</ArticleImagesProvider>
	);
};
